import { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  ClipboardCheck,
  ClipboardPen,
  Cpu,
  FileText,
  ListTodo,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import FlexBox from "components/common/FlexBox";
import useIsMobile from "components/hooks/useIsMobile";
import LinkRouter from "components/navigation/LinkRouter";
import { useListMemberDocuments } from "services/documents";
import { ListMemberDocumentsRequest } from "protogen/documents_service_pb";
import { DocumentType, DocumentScope } from "protogen/documents_pb";
import {
  getDocumentLink,
  isFormDefinitionDocument,
  isListDocument,
} from "components/documents/utils";
import { Document } from "protogen/documents_pb";

export type DocumentItem = {
  title: string;
  to: string;
};

export interface DocumentsProps {
  documents?: DocumentItem[];
  hideHeader?: boolean;
}

const getDocIcon = (document: Document) => {
  if (isFormDefinitionDocument(document)) {
    return <ClipboardPen size={16} strokeWidth={2} />;
  }
  if (document.documentType === DocumentType.FORM) {
    return <ClipboardCheck size={16} strokeWidth={2} />;
  }
  if (isListDocument(document)) {
    return <ListTodo size={16} strokeWidth={2} />;
  }
  return null;
};

const getDocLink = (document: Document) => {
  if (document.documentType === DocumentType.FORM) {
    const subPath = isFormDefinitionDocument(document) ? "create" : "complete";
    return `/forms/${subPath}/${encodeURIComponent(document.uuid)}`;
  }
  return getDocumentLink(document);
};

const DocumentRow = ({
  doc,
  icon,
}: {
  doc: DocumentItem;
  icon?: React.ReactNode;
}) => (
  <Box
    component={LinkRouter}
    to={doc.to}
    sx={(theme) => ({
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "12px 16px",
      borderRadius: "12px",
      border: `1px solid ${theme.palette.gray[4]}`,
      cursor: "pointer",
      textDecoration: "none",
      color: "inherit",
    })}
  >
    {icon ?? <Cpu size={16} strokeWidth={2} />}
    <Typography variant="body" color="text.primary">
      {doc.title}
    </Typography>
  </Box>
);

const SectionSubheading = ({
  label,
  uppercase = true,
}: {
  label: string;
  uppercase?: boolean;
}) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      textTransform: uppercase ? "uppercase" : "none",
      letterSpacing: "0.6px",
      color: "rgba(0,0,0,0.45)",
      marginLeft: "6px",
    }}
  >
    {label}
  </Typography>
);

const Documents = ({ documents = [], hideHeader = false }: DocumentsProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { request: listDocuments, data: documentsData } =
    useListMemberDocuments();

  useEffect(() => {
    listDocuments(
      new ListMemberDocumentsRequest({
        documentTypes: [
          DocumentType.NOTE,
          DocumentType.TASK_BLOCK,
          DocumentType.LIST,
          DocumentType.FORM,
        ],
      }),
    );
  }, []);

  const recentDocuments = (documentsData?.documents ?? [])
    .filter((d) => d.scope !== DocumentScope.FAMILY_BOT && !!d.title)
    .sort((a, b) => Number(b.lastUpdatedSec) - Number(a.lastUpdatedSec))
    .slice(0, 3);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {!hideHeader && (
        <FlexBox direction="row" justifyContent="space-between">
          <FlexBox direction="row" gap="8px" alignItems="center">
            <FileText size={20} color="#198282" />
            <Typography variant={isMobile ? "h3" : "h4"}>Documents</Typography>
          </FlexBox>
          <Button
            variant="text"
            sx={{ height: "auto", minWidth: "unset" }}
            onClick={() => navigate("/documents")}
          >
            See all
          </Button>
        </FlexBox>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <SectionSubheading
          label="Core memories that guide your agent's behavior"
          uppercase={false}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {documents.length === 0 ? (
            <Typography variant="body" color="text.secondary">
              These files shape how your Faye agent understands your household.
              They'll appear here once your agent starts learning about you.
            </Typography>
          ) : (
            documents.map((doc, index) => (
              <DocumentRow key={`${doc.to}-${index}`} doc={doc} />
            ))
          )}
        </Box>
      </Box>

      {recentDocuments.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <SectionSubheading label="Recent Documents" />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {recentDocuments.map((doc) => (
              <DocumentRow
                key={doc.uuid}
                doc={{
                  title: doc.title,
                  to: getDocLink(doc),
                }}
                icon={getDocIcon(doc) ?? <FileText size={16} strokeWidth={2} />}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Documents;
